import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './TicketDetails.module.css';
import { fetchTicketDetails, closeTicket } from '../services/ticketService';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../store/slices/notificationSlice';

const TicketDetails = ({ ticketId, onClose, onDataChange }) => {
    const dispatch = useDispatch();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [closeLoading, setCloseLoading] = useState(false);

    useEffect(() => {
        if (!ticketId) return;
        setLoading(true);
        setError(null);
        fetchTicketDetails(ticketId)
            .then((res) => {
                if (res.data && res.data.data) {
                    setTicket(res.data.data);
                } else {
                    setTicket(null);
                    setError('Ticket not found');
                }
            })
            .catch((err) => {
                setError(err.message || 'Failed to fetch ticket details');
                setTicket(null);
            })
            .finally(() => setLoading(false));
    }, [ticketId]);

    const formatDate = (dateString) => {
        return dateString;
    };

    const handleSendResponse = () => {
        if (!ticket || !ticket.phone_code || !ticket.phone) {
            dispatch(showWarningNotification('Customer phone number not available'));
            return;
        }

        // Format the phone number for WhatsApp
        const fullPhoneNumber = ticket.phone_code + ticket.phone;
        // const fullPhoneNumber = "+201280484011";

        // Create WhatsApp URL with a default message
        const message = encodeURIComponent('Hello, I\'m responding to your ticket.');

        // Use WhatsApp Web directly to ensure it opens the chat properly
        const whatsappWebUrl = `https://web.whatsapp.com/send?phone=${fullPhoneNumber}&text=${message}`;

        // Open WhatsApp Web in a new tab
        window.open(whatsappWebUrl, '_blank');
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const handleImageClick = (imgUrl) => {
        setFullscreenImage(imgUrl);
    };

    const handleCloseFullscreen = () => {
        setFullscreenImage(null);
    };

    const handleCloseTicket = async () => {
        if (!ticket || ticket.status === 'closed') return;

        setCloseLoading(true);
        try {
            await closeTicket(ticket.id);
            // Refresh ticket data to show updated status
            const res = await fetchTicketDetails(ticketId);
            if (res.data && res.data.data) {
                setTicket(res.data.data);
            }
            // Notify parent that data has changed and pass the ticket ID
            if (onDataChange) {
                onDataChange(ticket.id);
            }
            // Go back to the list with refreshed data
            if (onClose) {
                onClose();
            }
            dispatch(showSuccessNotification('Ticket closed successfully!'));
        } catch (error) {
            console.error('Error closing ticket:', error);
            dispatch(showErrorNotification('Failed to close ticket: ' + error.message));
        } finally {
            setCloseLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.ticketDetailsContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button className={styles.backButton} onClick={onClose}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h2 className={styles.headerTitle}>Loading...</h2>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.ticketInfo}>
                        <p>Loading ticket details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !ticket) {
        return (
            <div className={styles.ticketDetailsContainer}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <button className={styles.backButton} onClick={onClose}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h2 className={styles.headerTitle}>Ticket Not Found</h2>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.ticketInfo}>
                        <p>{error || 'This ticket is no longer available or has been moved to a different category.'}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.ticketDetailsContainer}>
            {/* Fullscreen Image Modal */}
            {fullscreenImage && (
                <div className={styles.fullscreenOverlay} onClick={handleCloseFullscreen}>
                    <div className={styles.fullscreenImageWrapper} onClick={e => e.stopPropagation()}>
                        <button className={styles.fullscreenCloseButton} onClick={handleCloseFullscreen}>
                            &times;
                        </button>
                        <img src={fullscreenImage} alt="Fullscreen Attachment" className={styles.fullscreenImage} />
                    </div>
                </div>
            )}
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backButton} onClick={handleClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h2 className={styles.headerTitle}>Ticket Details</h2>
                </div>
                <div className={styles.headerRight}>
                    <span className={`${styles.statusBadge} ${styles[ticket.status]}`}>
                        {ticket.status === 'pending' ? 'Pending' : 'Resolved'}
                    </span>
                    {ticket.status === 'pending' && (
                        <button
                            className={styles.closeTicketButton}
                            onClick={handleCloseTicket}
                            disabled={closeLoading}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 12l2 2 4-4" />
                                <path d="M21 12c-1 0-2.4-.4-3.5-1.5S16 8 16 7s.4-2.4 1.5-3.5S20 2 21 2s2.4.4 3.5 1.5S26 6 26 7s-.4 2.4-1.5 3.5S22 12 21 12z" />
                            </svg>
                            {closeLoading ? 'Closing...' : 'Close Ticket'}
                        </button>
                    )}
                    {ticket.status === 'pending' && (
                        <button className={styles.sendResponseButton} onClick={handleSendResponse}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 21l1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z" />
                                <path d="M17 7a8.38 8.38 0 0 1 .9 3.8 8.5 8.5 0 0 1-3.8 3.8" />
                                <path d="M10 10h.01" />
                                <path d="M14 10h.01" />
                            </svg>
                            WhatsApp
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className={styles.content}>
                <div className={styles.ticketInfo}>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Customer:</span>
                        <span className={styles.infoValue}>{ticket.customer}</span>
                    </div>

                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Date:</span>
                        <span className={styles.infoValue}>{formatDate(ticket.date)}</span>
                    </div>

                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Category:</span>
                        <span className={styles.infoValue}>{ticket.category}</span>
                    </div>

                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Subcategory:</span>
                        <span className={styles.infoValue}>{ticket.subcategory || 'N/A'}</span>
                    </div>
                </div>

                {/* Description */}
                <div className={styles.descriptionSection}>
                    <h3 className={styles.sectionTitle}>Description</h3>
                    <div className={styles.descriptionContainer}>
                        <p className={styles.descriptionText}>{ticket.description}</p>
                    </div>
                </div>

                {/* Attachments */}
                <div className={styles.attachmentsSection}>
                    <h3 className={styles.sectionTitle}>Attachments</h3>
                    <div className={styles.attachmentsGrid}>
                        {ticket.images && ticket.images.length > 0 ? (
                            ticket.images.map((img, index) => (
                                <div key={img._id || index} className={styles.attachmentItem}>
                                    <img
                                        src={img.image}
                                        alt={`Attachment ${index + 1}`}
                                        className={styles.attachmentImage}
                                        onClick={() => handleImageClick(img.image)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            ))
                        ) : (
                            <div className={styles.attachmentItem}>
                                <span>No attachments</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketDetails; 